import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State, Folder, File, FoldersResponse } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {
  selectedFolder: null,
  selectedFile: null,
  updatedFiles: null,
  openFolderId: parseInt(localStorage.getItem('folder_id') || '0'),
  folderPath: null,
  isAIGenerated: false,
  aiContent: null,
  isLoading: false,
  error: null,
};

const slice = createSlice({
  name: 'fileManager',
  initialState,
  reducers: {
    setSelectedFolder(state, action: PayloadAction<Folder | null>) {
      state.selectedFolder = action.payload;
    },
    setSelectedFile(state, action: PayloadAction<File | null>) {
      state.selectedFile = action.payload;
    },
    setUpdatedFiles(state, action: PayloadAction<any>) {
      state.updatedFiles = action.payload;
    },
    setOpenFolderId(state, action: PayloadAction<number>) {
      state.openFolderId = action.payload;
      localStorage.setItem('folder_id', action.payload.toString());
    },
    setFolderPath(state, action: PayloadAction<any>) {
      state.folderPath = action.payload;
    },
    setIsAIGenerated(state, action: PayloadAction<boolean>) {
      state.isAIGenerated = action.payload;
    },
    setAiContent(state, action: PayloadAction<any>) {
      state.aiContent = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
});

export const api = createApi({
  reducerPath: 'fileManagerApi',
  baseQuery,
  tagTypes: ['Folders', 'Files'],
  endpoints: builder => ({
    // Get folders and files
    getFolders: builder.query<FoldersResponse, any>({
      query: ({ id, search, sort, order }) => {
        const folderId = id || localStorage.getItem('folder_id') || 0;
        const baseUrl = `${endpoints.folders.url}`;
        const queryParams = new URLSearchParams();
        queryParams.append('folder_id', folderId.toString());

        if (search) {
          queryParams.append('search', search);
        }
        // Only add sort parameter if it's explicitly provided and valid
        if (sort && ['name', 'date'].includes(sort)) {
          queryParams.append('sort', sort);
        }
        if (order && ['asc', 'desc'].includes(order)) {
          queryParams.append('order', order);
        }

        return {
          url: `${baseUrl}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Folders'],
    }),

    // Get folder path for breadcrumb
    getFolderPath: builder.query<any, number>({
      query: folderId => ({
        url: `${endpoints.FOLDER_PATH}/${folderId}`,
        method: 'GET',
      }),
    }),

    // Get single file
    getFile: builder.query<any, string>({
      query: id => ({
        url: `${endpoints.GET_FILE}/${id}`,
        method: 'GET',
      }),
    }),

    // Add folder
    addFolder: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.addFolder.url,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Folders'],
    }),

    // Update folder
    updateFolder: builder.mutation<any, any>({
      query: ({ folder_id, ...data }) => ({
        url: `${endpoints.UPDATE_FOLDER}/${folder_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Folders'],
    }),

    // Delete folder
    deleteFolder: builder.mutation<any, number>({
      query: folderId => ({
        url: `${endpoints.DELETE_FOLDER}/${folderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Folders'],
    }),

    // Add file
    addFile: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.addFile.url,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),

    // Update file
    updateFile: builder.mutation<any, any>({
      query: ({ file_id, ...data }) => ({
        url: `${endpoints.UPDATE_FILE}/${file_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),

    // Delete file
    deleteFile: builder.mutation<any, number>({
      query: fileId => ({
        url: `${endpoints.DELETE_FILE}/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Files'],
    }),

    // Cut/Copy file
    cutCopyFile: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.CUT_COPY_FILE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),

    // Cut/Copy folder
    cutCopyFolder: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.CUT_COPY_FOLDER,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Folders'],
    }),

    // Bulk operations
    bulkDeleteFolderFiles: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.FOLDER_FILES_BULK_DELETE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Folders', 'Files'],
    }),

    bulkCutCopyPaste: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.BULK_CUT_COPY_PASTE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Folders', 'Files'],
    }),

    // Bulk create folders
    bulkCreateFolders: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.BULK_CREATE_FOLDERS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Folders'],
    }),

    // Generate SEO tags with AI
    generateSeoTagsWithAi: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.GENERATE_SEO_TAGS_WITH_AI,
        method: 'POST',
        body: data,
      }),
    }),

    // Search files and folders
    searchFilesAndFolders: builder.query<any, string>({
      query: query => ({
        url: `${endpoints.SEARCH_FILES}?query=${encodeURIComponent(query)}`,
        method: 'GET',
      }),
    }),
  }),
});

export const { actions: FileManagerActions } = slice;

export const useFileManagerSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: FileManagerActions,
    useGetFoldersQuery: api.useLazyGetFoldersQuery,
    useGetFolderPathQuery: api.useLazyGetFolderPathQuery,
    useGetFileQuery: api.useLazyGetFileQuery,
    useAddFolderMutation: api.useAddFolderMutation,
    useUpdateFolderMutation: api.useUpdateFolderMutation,
    useDeleteFolderMutation: api.useDeleteFolderMutation,
    useAddFileMutation: api.useAddFileMutation,
    useUpdateFileMutation: api.useUpdateFileMutation,
    useDeleteFileMutation: api.useDeleteFileMutation,
    useCutCopyFileMutation: api.useCutCopyFileMutation,
    useCutCopyFolderMutation: api.useCutCopyFolderMutation,
    useBulkDeleteFolderFilesMutation: api.useBulkDeleteFolderFilesMutation,
    useBulkCutCopyPasteMutation: api.useBulkCutCopyPasteMutation,
    useBulkCreateFoldersMutation: api.useBulkCreateFoldersMutation,
    useGenerateSeoTagsWithAiMutation: api.useGenerateSeoTagsWithAiMutation,
    useSearchFilesAndFoldersQuery: api.useLazySearchFilesAndFoldersQuery,
  };
};
